/* eslint-disable @nx/enforce-module-boundaries */
import {
  Component,
  Inject,
  OnInit,
  computed,
  inject,
  signal,
} from '@angular/core';
import {
  MAT_DIALOG_DATA,
  MatDialogModule,
  MatDialogRef,
} from '@angular/material/dialog';
import { MatButtonModule } from '@angular/material/button';
import { VehicleTypesService } from '../../../api/services/vehicle-types.service';
import {
  LoaderIndicatorDirective,
  LoaderService,
  NotificationService,
} from '@t-llevo/common-ui';
import { HttpErrorResponse } from '@angular/common/http';
import { DialogHeaderComponent } from 'admin-drivers/src/app/common/dialog/components/dialog-header/dialog-header.component';
import {
  FormControl,
  NonNullableFormBuilder,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import { toSignal } from '@angular/core/rxjs-interop';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatSelectModule } from '@angular/material/select';
import { ImageInputComponent } from 'admin-drivers/src/app/common/components/image-input/image-input.component';
import { FormDialogData } from 'admin-drivers/src/app/common/models/FormDialogData';
import { VehicleType } from 'admin-drivers/src/app/common/models/VehicleType';
import { finalize } from 'rxjs';
import { MatCheckboxModule } from '@angular/material/checkbox';

@Component({
  selector: 't-llevo-vehicle-types-add',
  standalone: true,
  imports: [
    MatDialogModule,
    MatButtonModule,
    LoaderIndicatorDirective,
    DialogHeaderComponent,
    MatFormFieldModule,
    MatInputModule,
    ReactiveFormsModule,
    MatSelectModule,
    ImageInputComponent,
    MatCheckboxModule,
  ],
  styles: `
    @import '../../../utils/styles/baseDialog.scss';
    @import '../../../utils/styles/formGroup.scss';

    t-llevo-image-input {
      display: flex;
      width: 100%;
      min-height: 200px;
      max-height: 350px;
    }
  `,
  providers: [LoaderService],
  templateUrl: './vehicle-types-form.component.html',
})
export class VehicleTypesFormComponent implements OnInit {
  private _vehicleTypesSrv = inject(VehicleTypesService);
  private _notifSrv = inject(NotificationService);
  private _loaderSrv = inject(LoaderService);

  protected energySources = this._vehicleTypesSrv.energySources;
  protected loading = toSignal(this._loaderSrv.loaderState$);
  protected frm = this._fb.group({
    name: ['', Validators.required],
    passengers: [1, [Validators.required, Validators.min(1)]],
    energySource: ['', Validators.required],
    photo: this._fb.control<File | null>(null, [Validators.required]),
    available: true,
  });

  private _edit = signal(false);
  protected dialogTitle = computed(
    () => `${this._edit() ? 'Editar' : 'Adicionar'} tipo de vehículo`,
  );
  protected submitBtn = computed(() => (this._edit() ? 'Editar' : 'Adicionar'));

  constructor(
    private _dialogRef: MatDialogRef<VehicleTypesFormComponent>,
    @Inject(MAT_DIALOG_DATA) public formData: FormDialogData<VehicleType> | undefined,
    private _fb: NonNullableFormBuilder,
  ) {}

  ngOnInit(): void {
    if (this.formData && this.formData.edit) {
      this._loaderSrv.show();
      this._edit.set(true);
      this.assignData();
      this._vehicleTypesSrv
        .getVehicleImageByUrl(this.formData.data.photo)
        .pipe(finalize(() => this._loaderSrv.hide()))
        .subscribe((data) => this.assignImage(data));
    }
  }

  private assignData() {
    this.name.setValue(this.formData!.data.name);
    this.passengers.setValue(this.formData!.data.passengers);
    this.energySource.setValue(this.formData!.data.energySource);
    this.available.setValue(this.formData!.data.available);
  }

  private assignImage(imgBlob: Blob) {
    const splitedPhotoUrl = this.formData!.data.photo.split('/');
    const imgName = splitedPhotoUrl[splitedPhotoUrl.length - 1];

    this.frm.controls['photo'].setValue(new File([imgBlob], imgName));
  }

  onSubmit() {
    if (this.frm.valid) {
      this._loaderSrv.show();

      const params = {
        ...this.frm.getRawValue(),
        passengers: Number(this.frm.getRawValue().passengers),
        photo: this.frm.controls['photo'].value as File,
      };

      const request = this._edit()
        ? this._vehicleTypesSrv.updateVehicleType(this.formData!.data.id, params)
        : this._vehicleTypesSrv.createVehicleType(params);

      request.subscribe({
        next: () => {
          const notificationMsg = `Tipo de vehículo ${this._edit() ? 'editado' : 'creado'}`;
          this._loaderSrv.hide();
          this._notifSrv.dispatch(notificationMsg, 'success');
          this._dialogRef.close(true);
        },
        error: (err: HttpErrorResponse) => {
          this._loaderSrv.hide();
          this._notifSrv.dispatch(err.error.message, 'error');
        },
      });
    }
  }

  get name() {
    return this.frm.controls['name'];
  }

  get photo() {
    return this.frm.controls['photo'] as FormControl<File | null>;
  }

  get passengers() {
    return this.frm.controls['passengers'];
  }

  get energySource() {
    return this.frm.controls['energySource'];
  }

  get available() {
    return this.frm.controls['available'];
  }
}
